/* eslint-disable camelcase */
import {
  DATE_RANGE_TYPE,
  DATE_UNITS,
  ALL_DEVICE_TYPES,
  RadioTextFilterOperation,
} from '@hotjar/global-filters';
import type {
  DateRange,
  DEVICE_TYPE,
  LargeListFilterValue,
  PageURLFilterValue,
  KeywordResponseFilterValue,
} from '@hotjar/global-filters';
import type { CountryCode } from '@hotjar/country-lib';
import type { RatingFilterValue } from '../../../components/IncomingFeedback/ResponsesFilters/RatingFilter/RatingFilterType';
import { RATING_TYPE } from '../../../components/IncomingFeedback/ResponsesFilters/RatingFilter/RatingFilterType';
import type { HasEmailFilterValue } from '../../../components/IncomingFeedback/ResponsesFilters/HasEmailFilter/HasEmailType';
import type { HasMessageFilterValue } from '../../../components/IncomingFeedback/ResponsesFilters/HasFeedbackMessageFilter/HasFeedbackMessageType';
import type { FeedbackResponseFilters } from '../../../components/IncomingFeedback/ResponsesFilters/ResponsesFilters';

export enum IncomingFeedbackFilters {
  DATE = 'date',
  DEVICE = 'device',
  COUNTRY = 'country',
  RATING = 'emotions',
  PAGE = 'page',
  KEYWORD = 'keyword',
  FEEDBACK_MESSAGE = 'has_message',
  EMAIL = 'has_email',
}

export interface DeviceResponseFilter {
  type: IncomingFeedbackFilters.DEVICE;
  value: DEVICE_TYPE[];
}

export interface DateResponseFilter {
  type: IncomingFeedbackFilters.DATE;
  value: DateRange | undefined;
}

export interface PageResponseFilter {
  type: IncomingFeedbackFilters.PAGE;
  value: PageURLFilterValue;
}

export interface CountryResponseFilter {
  type: IncomingFeedbackFilters.COUNTRY;
  value: LargeListFilterValue<CountryCode> | undefined;
}

export interface RatingResponseFilter {
  type: IncomingFeedbackFilters.RATING;
  value: RatingFilterValue;
}

export interface HasMessageResponseFilter {
  type: IncomingFeedbackFilters.FEEDBACK_MESSAGE;
  value: HasMessageFilterValue;
}

export interface HasEmailResponseFilter {
  type: IncomingFeedbackFilters.EMAIL;
  value: HasEmailFilterValue;
}

export interface KeywordResponseFilter {
  type: IncomingFeedbackFilters.KEYWORD;
  value: KeywordResponseFilterValue;
}

export type ResponseFilter =
  | DateResponseFilter
  | DeviceResponseFilter
  | CountryResponseFilter
  | HasMessageResponseFilter
  | RatingResponseFilter
  | PageResponseFilter
  | HasEmailResponseFilter
  | KeywordResponseFilter;

export const DEFAULT_DATE_FILTER: DateResponseFilter = {
  type: IncomingFeedbackFilters.DATE,
  value: {
    type: DATE_RANGE_TYPE.RELATIVE,
    amount: 30,
    unit: DATE_UNITS.DAYS,
  },
};

export const DEFAULT_DEVICE_FILTER_VALUE = [...ALL_DEVICE_TYPES];

export const DEFAULT_DEVICE_FILTER: DeviceResponseFilter = {
  type: IncomingFeedbackFilters.DEVICE,
  value: DEFAULT_DEVICE_FILTER_VALUE,
};

export const DEFAULT_RATING_FILTER: RatingResponseFilter = {
  type: IncomingFeedbackFilters.RATING,
  value: [
    RATING_TYPE.HATE,
    RATING_TYPE.DISLIKE,
    RATING_TYPE.NEUTRAL,
    RATING_TYPE.LIKE,
    RATING_TYPE.LOVE,
  ],
};

export const GET_PAGE_FILTER = (
  pageUrl: string,
  pageOperation: 'simple_match' | 'exact',
): PageResponseFilter => ({
  type: IncomingFeedbackFilters.PAGE,
  value: {
    value: pageUrl,
    operation:
      pageOperation === 'simple_match'
        ? RadioTextFilterOperation.CONTAINS
        : RadioTextFilterOperation.IS,
  },
});

export const THREE_MONTHS_DATE_FILTER: DateResponseFilter = {
  type: IncomingFeedbackFilters.DATE,
  value: {
    type: DATE_RANGE_TYPE.RELATIVE,
    amount: 3,
    unit: DATE_UNITS.MONTHS,
  },
};

export const DEFAULT_INCOMING_FEEDBACK_RESPONSES_FILTERS: FeedbackResponseFilters = {
  [IncomingFeedbackFilters.DATE]: { ...DEFAULT_DATE_FILTER },
  [IncomingFeedbackFilters.DEVICE]: undefined,
  [IncomingFeedbackFilters.COUNTRY]: undefined,
  [IncomingFeedbackFilters.PAGE]: undefined,
  [IncomingFeedbackFilters.KEYWORD]: undefined,
  [IncomingFeedbackFilters.RATING]: { ...DEFAULT_RATING_FILTER },
  [IncomingFeedbackFilters.FEEDBACK_MESSAGE]: undefined,
  [IncomingFeedbackFilters.EMAIL]: undefined,
};
